import React, { useEffect, useState } from 'react'
import { Box, Button, FormControl, FormHelperText, FormLabel, Input, Spinner, Switch, Tag, TagCloseButton, TagLabel, Text, useDisclosure, Tooltip } from '@chakra-ui/react'
import { InfoIcon } from '@chakra-ui/icons'
import Licensee from '@/model/licensee.mjs';
import { licenseDetailsNewApi } from '@/api/apis';
import { getLicenseType, validateRedirectUrl } from '@/utils/utils';
import { CANCEL_LABEL, CREATE_STORE_LABELS, typeList } from '@/utils/constants';
import { createNewStoreSchema } from '@/model/yupSchema.mjs';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { callToCreateStore, setDomainError, setEmailError, setStoreLicenseError } from '@/redux/app-slice';
import { v4 as uuidv4 } from 'uuid';
import FFLDetailModal from '@/explore/ffl-detail/FFLDetailModal';
import { getDomainExistError, getEmailExistError, getStoreLicenseExistError, getIsCreateStoreLoading, getStoreRefresh } from '@/redux/app-selectors';
import { LicenseDetail } from '@/model/builders/ffl-get-license-details.mjs';
import FFLRecognizer from '@/utils/regexMatcher';
import { formatLicenseKey, getEightDigitLicenseKey } from '@/explore/ffl-detail/utils.mjs';

export interface CreateStoreProps {
    onCreateStoreClose: () => void;
}

const CreateStore = ({ onCreateStoreClose }: CreateStoreProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [license, setLicense] = useState<any>();
    const [licenseKey, setLicenseKey] = useState<string>('');
    const [notFoundError, setNotFoundError] = useState<string>('');
    const dispatch = useDispatch();
    const [isExternal, setIsExternal] = useState<boolean>(false);
    const [isCFD, setIsCFD] = useState<boolean>(false);
    const [isMultiTenant, setIsMultitenant] = useState<boolean>(false)
    const [isRedirectReassignUrlChecked, setIsRedirectReassignUrlChecked] = useState<boolean>(false);
    const [redirectReassignmentUrl, setRedirectReassignmentUrl] = useState<string>('');
    const [redirectUrlError, setRedirectUrlError] = useState<string>('');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isRefresh, setIsRefresh] = useState<boolean>(false);
    const [isEditingEnabled, setIsEditingEnabled] = useState<boolean>(false);
    const [storeDomainValues, setStoreDomainValues] = useState<string[]>([]);
    const isCreateStoreLoading = getIsCreateStoreLoading();
    const emailError = getEmailExistError();
    const domainError = getDomainExistError();
    const storeLicenseError = getStoreLicenseExistError();
    const storeRefresh = getStoreRefresh();
    const [storeDomainVal, setStoreDomainVal] = useState<string>('');


    useEffect(() => {
      if(storeRefresh){
        onCreateStoreClose()
      }
    }, [storeRefresh])

    useEffect(() => {
      if(isRefresh) {
        setLicense({});
      }
    }, [isRefresh])
    
    const {
        handleSubmit,
        register,
        setValue,
        getValues,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(createNewStoreSchema),
        mode: 'onChange',
        values: {
            store_license_key: '',
            sender_email: '',
        }
    });

    function getLicenseData() {
        const recognizer = new FFLRecognizer();
                            const match = recognizer.matches(licenseKey.trim());
                            const value =
                              match && match.length > 3
                                ? `${match[1]}${match[2]}${match[3]}`
                                : licenseKey.trim();
        licenseDetailsNewApi(
            value
        ).then((data: LicenseDetail) => {
            if(data){
              setIsLoading(false);
              setIsRefresh(false);
              setLicense(data);
              setIsEditingEnabled(false)

            }
            else{
              setIsLoading(false);  
              setNotFoundError('License number not found.')
              setLicense({});
            }
        }).catch((e: any) => {
            console.log('Details getting error :', String(e));
            if (e && e.response && e.response.status && e.response.status === 404) {
                setNotFoundError('License number not found.')
            }
            setIsLoading(false);
            setIsRefresh(false);
        });
    }

    const getMailingAddress = () => {
        if (license && license.address) {
            return `${license.address.street}, ${license.address.city}, ${license.address.state}, ${license.address.zipCode}`
        }
        return '-'
    }

    const getContactName = () => {
        if (license && license.contactDetails && license.contactDetails.length > 0) {
            const primaryDetails = license.contactDetails.find((contactDetail: any) => contactDetail.isPrimary === true)
            if (primaryDetails && primaryDetails.name) {
                return primaryDetails.name
            }
            return "-"
        }
        return '-'
    }

    function renderLicenseId() {
        return (
            <div className="transfer-form__search-result">
                <Input
                    type="text"
                    maxLength={20}
                    {...register('store_license_key', {
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                            setNotFoundError('');
                            dispatch(setStoreLicenseError(''))
                            setIsRefresh(true)
                            setIsEditingEnabled(true)
                            setLicenseKey(e.target.value)

                        }
                    })}
                    name="store_license_key"
                    placeholder="Search FFL License Number"
                    value={licenseKey}
                />
                <Button
                    onClick={() => {
                        if (!errors?.store_license_key) {
                            setIsLoading(true);
                            setLicense({});
                            getLicenseData();
                        }
                    }}
                    isDisabled={licenseKey === ''}
                    className="transfer-form__search-btn store-license-btn"
                >
                    {isLoading && !errors?.store_license_key ? (
                        <Spinner size="sm" />
                    ) : (
                        <img src="/images/ui/search-icon-blue.svg" />
                    )}
                </Button>
            </div>
        );
    }
    const createHandler = () => {
        let { store_license_key, sender_email } = getValues()
        let updatedDomainValues: any[] = storeDomainValues;
        if (storeDomainVal && !storeDomainValues.includes(storeDomainVal)) {
            updatedDomainValues = [...storeDomainValues, storeDomainVal];
            setStoreDomainValues(updatedDomainValues);
        }
        store_license_key = getEightDigitLicenseKey(store_license_key)
        const domains = updatedDomainValues && updatedDomainValues.length > 0 ? updatedDomainValues.join(',') : '';
        if (updatedDomainValues && !updatedDomainValues.length) {
            dispatch(setDomainError('Store doamin is required!'));
            return;
        }
        setStoreDomainVal('')
        if (isMultiTenant && isRedirectReassignUrlChecked && !redirectReassignmentUrl) {
            setRedirectUrlError('Enter URL or disable the toggle');
            return;
        }
        const payload = {
            store_id: uuidv4(),
            seller_email: sender_email.toLowerCase(),
            name: license?.primaryBusinessName,
            store_license_key: store_license_key,
            is_external: isExternal,
            is_cfd_workflow: isCFD,
            store_domain: domains,
            is_multi_tenant: isMultiTenant,
            ...(redirectReassignmentUrl && {redirect_reassign_url:  redirectReassignmentUrl})
        }
        dispatch(callToCreateStore(payload))
    }

    const onChangeStoreType = () => {
        return () => {
        setIsExternal(!isExternal);
    }
    }
    const onChangeCFD = () => {
        return () => {
            setIsCFD(!isCFD);
        }
    }
    const onChangeIsMultitenant = () => {
        if (isMultiTenant) {
            // when I Uncheck
            setIsRedirectReassignUrlChecked(false);
            setRedirectReassignmentUrl('');
            setRedirectUrlError('');
        }
        setIsMultitenant(!isMultiTenant)
    }
    const onChangeRedirectReassignUrl = () => {
        if (isRedirectReassignUrlChecked) {
            // When unchecking, clear error and URL
            setRedirectUrlError('');
            setRedirectReassignmentUrl('');
        }
        setIsRedirectReassignUrlChecked(!isRedirectReassignUrlChecked);
    }
    const onChangeRedirectUrl = (value: string) => {
        setRedirectReassignmentUrl(value.trim());
        setRedirectUrlError(validateRedirectUrl(value.trim()));
    }
    const openFFLDealerDetails = () => {
        if(license?.dealerId) {
            const linkUrl = `/dealers-search/${license?.dealerId}`;
            window.open(linkUrl, '_blank');
        }
    }

    const onClickAddMoreDomain = () => {
        const store_domain = storeDomainVal;
        const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,11}$/;

        if (storeDomainValues.length >= 20) {
            dispatch(setDomainError('Maximum 20 domains allowed'));
            return;
        }

        if (!store_domain) {
            dispatch(setDomainError('Enter domain name!'));
            return;
        }

        if (!domainRegex.test(store_domain)) {
            dispatch(setDomainError('Invalid domain name'));
            return;
        }

        dispatch(setDomainError(''));
        setStoreDomainValues([...storeDomainValues, store_domain]);
        setStoreDomainVal('');
    }
    const handleRemoveDoaminTag = (value: string) => {
        storeDomainValues.splice(storeDomainValues.indexOf(value), 1);
        setStoreDomainValues([...storeDomainValues]);
    }

    const onChangeDomain = async (value: string) => {
        const isDuplicate = storeDomainValues.includes(value);
        const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,11}$/;
        setStoreDomainVal(value);
        
        if (isDuplicate) {
            dispatch(setDomainError('This domain already exists'));
        } else if (!domainRegex.test(value)) {
            dispatch(setDomainError('Please enter a valid domain'));
        } else {
            dispatch(setDomainError(''));
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(createHandler)}>
                <Box className='add-store-container'>
                    <Box className='create-store-col store-left-section'>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.LICENSE_ID_LABEL}
                            </FormLabel>
                            <Box>
                                {renderLicenseId()}
                                {(errors.store_license_key) && (
                                    <Text as="sub" color="red">
                                        {errors.store_license_key.message}
                                    </Text>
                                )}
                                {(notFoundError) && (
                                    <Text as="sub" color="red">
                                        {notFoundError}
                                    </Text>
                                )}
                                {licenseKey !== '' && storeLicenseError && (
                                    <Text as="sub" color="red">
                                        {storeLicenseError}
                                    </Text>
                                )}

                                {!isEditingEnabled && (license && license?.licenses?.length > 0 ? (
                                    <FormHelperText className="transfer-form__helper-text">
                                        <span className="transfer-details-value" style={{ paddingTop: '1rem' }}>
                                            <a style={{cursor:'pointer'}}
                                                onClick={openFFLDealerDetails}
                                                className="transfer-details__link"
                                            >
                                                {license?.licenses.find((license:any) => license?.licenseKey === getEightDigitLicenseKey(licenseKey))?.fullLicenseKey}
                                            </a>
                                        </span>
                                    </FormHelperText>
                                ) : (
                                    <FormHelperText className="transfer-form__helper-text">
                                        {CREATE_STORE_LABELS.LICENSE_NUMBER_HELPER_TEXT}
                                    </FormHelperText>
                                ))}

                            </Box>
                        </FormControl>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.LICENSE_TYPE_LABEL}
                            </FormLabel>
                            <Box display="flex" flexDirection="column">
                                <Box className="transfer-form__license-info">
                                    {getLicenseType(typeList, (license && license?.licenses?.length) ?
                                    license?.licenses.find((license:any) => license?.licenseKey === getEightDigitLicenseKey(licenseKey))?.type : '-')}
                                </Box>
                            </Box>
                        </FormControl>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.BUSINESS_NAME_LABEL}
                            </FormLabel>
                            <Box display="flex" flexDirection="column">
                                <Box className="transfer-form__license-info">
                                    {license?.primaryBusinessName || '-'}
                                </Box>
                            </Box>
                        </FormControl>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.IS_EXTERNAL_LABEL} :
                                <Switch
                                    size="md"
                                    onChange={onChangeStoreType()}
                                    disabled={false}
                                    style={{ marginLeft: '10px' }}
                                    isChecked={isExternal}
                                />
                            </FormLabel>
                        </FormControl>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.CFD_LABEL} :
                                <Switch
                                    size="md"
                                    onChange={onChangeCFD()}
                                    disabled={false}
                                    style={{ marginLeft: '10px' }}
                                    isChecked={isCFD}
                                />
                            </FormLabel>
                        </FormControl>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.IS_MULTITENANT_LABEL} :
                                <Switch
                                    size="md"
                                    onChange={onChangeIsMultitenant}
                                    style={{ marginLeft: '10px' }}
                                    isChecked={isMultiTenant}
                                />
                            </FormLabel>
                        </FormControl>
                        {isMultiTenant && (
                            <FormControl className="transfer-form__item">
                                <FormLabel className="transfer-form__label store_form_label">
                                    {CREATE_STORE_LABELS.CUSTOM_REASSIGNMENT_URL_MULTI_VENDOR}
                                    <Tooltip 
                                        label={CREATE_STORE_LABELS.REASSIGNMENT_REDIRECT_URL_INFO_LABEL}
                                        placement="top"
                                        hasArrow
                                        color="black"
                                    >
                                        <InfoIcon style={{ marginLeft: '5px', cursor: 'pointer', fontSize: '13px', top: '-2px', position: 'relative' }} />
                                    </Tooltip> :
                                    <Switch
                                        size="md"
                                        onChange={onChangeRedirectReassignUrl}
                                        style={{ marginLeft: '10px' }}
                                        isChecked={isRedirectReassignUrlChecked}
                                    />
                                </FormLabel>
                            </FormControl>
                        )}
                        { isRedirectReassignUrlChecked && (
                            <FormControl className="transfer-form__item">
                                <Input
                                    placeholder="Enter Redirect Reassignment URL"
                                    value={redirectReassignmentUrl}
                                    onChange={(e) => onChangeRedirectUrl(e.target.value)}
                                />
                                {redirectUrlError && (
                                    <Text as="sub" color="red">
                                        {redirectUrlError}
                                    </Text>
                                )}
                            </FormControl>
                        )}
                        
                    </Box>
                    <Box className='create-store-col'>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.CONTACT_NAME_LABEL}
                            </FormLabel>
                            <Box display="flex" flexDirection="column">
                                <Box className="transfer-form__license-info">
                                    {license?.contact?.value|| '-'}
                                </Box>
                            </Box>
                        </FormControl>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.MAILING_ADDRESS_LABEL}
                            </FormLabel>
                            <Box display="flex" flexDirection="column">
                                <Box className="transfer-form__license-info">
                                    {getMailingAddress()}
                                </Box>
                            </Box>
                        </FormControl>
                        <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}>
                            <FormControl className="transfer-form__item" sx={{ paddingBottom: '0.5rem' }}>
                                <FormLabel className="transfer-form__label store_form_label">
                                    {CREATE_STORE_LABELS.STORE_DOMAIN_LABEL}
                                </FormLabel>
                                <Box className='sender-email-input-container'>
                                    <Box className='transfer-form__search-result'>
                                        <Input
                                            value={storeDomainVal}
                                            placeholder='Enter Store Domain'
                                            onChange={(event) => onChangeDomain(event.target.value)}
                                            
                                        />
                                    </Box>
                                </Box>
                                {/* {errors.store_domain && (
                                    <Text as="sub" color="red">
                                        {errors.store_domain.message}
                                    </Text>
                                )} */}
                                {domainError && (
                                    <Text as="sub" color="red">
                                        {domainError}
                                    </Text>
                                )}

                            </FormControl>
                            <button
                                className="add-more-text more-domain"
                                disabled={domainError !== '' || storeDomainVal === ''}
                                onClick={onClickAddMoreDomain}
                            >
                                +Add More
                            </button>
                            <Box className='left-inner-clips-box' sx={{ marginTop: '0.5rem' }}>
                                {storeDomainValues && storeDomainValues.length > 0 && storeDomainValues.map((domainVal: string, index: number) => (
                                    <Tag className='chip-box' key={`domain_${index}`}>
                                        <TagLabel className='tag-label'>{domainVal}</TagLabel>
                                        <TagCloseButton
                                            padding={'0px'}
                                            fontSize={'15px'}
                                            color={'#436175'}
                                            onClick={() => handleRemoveDoaminTag(domainVal)}
                                        />
                                    </Tag>
                                ))}
                            </Box>
                        </Box>
                        <FormControl className="transfer-form__item">
                            <FormLabel className="transfer-form__label store_form_label">
                                {CREATE_STORE_LABELS.SENDER_EMAIL_LABEL}
                            </FormLabel>
                            <Box className='sender-email-input-container'>
                                <Box className='transfer-form__search-result'>
                                    <Input
                                        placeholder='Enter Sender Email'
                                        {...register('sender_email', {
                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                                setValue('sender_email', e.target.value)
                                                dispatch(setEmailError(''));
                                                if (e.target.value === '') setValue('sender_email', '');
                                            }
                                        })}
                                    />
                                </Box>
                                <Text className="email-text">{CREATE_STORE_LABELS.EMAIL_DOMAIN_LABEL}</Text>
                            </Box>
                            {errors.sender_email && (
                                <Text as="sub" color="red">
                                    {errors.sender_email.message}
                                </Text>
                            )}
                            {emailError && (
                                <Text as="sub" color="red">
                                    {emailError}
                                </Text>
                            )}
                        </FormControl>
                        <FormControl className="transfer-form__item" style={{ paddingBottom: '0px' }}>
                            <Box className="store_create_action_row">
                                <Button
                                    colorScheme="blue"
                                    background="#ffffff"
                                    color="#0C1E46"
                                    border="1px solid #0C1E46"
                                    padding="8px 11px"
                                    marginRight="20px"
                                    onClick={()=> {
                                        onCreateStoreClose()
                                        dispatch(setEmailError(''))
                                        dispatch(setDomainError(''));
                                        dispatch(setStoreLicenseError(''))
                                    }}
                                    isDisabled={isCreateStoreLoading}
                                >
                                    {CANCEL_LABEL}
                                </Button>
                                <Button
                                    colorScheme="blue"
                                    background="#0C1E46"
                                    padding="8px 20px"
                                    isLoading={isCreateStoreLoading}
                                    isDisabled={(!license?.fullLicenseKeys?.length) || Object.keys(errors).length > 0 || domainError !== '' || redirectUrlError !== '' }
                                    type="submit"
                                >
                                    {CREATE_STORE_LABELS.ONBOARD_STORE_LABEL}
                                </Button>
                            </Box>
                        </FormControl>
                    </Box>
                </Box>
            </form>
            
        </>
    )
}

export default CreateStore
